import { useCallback } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { supabaseData } from '../../utils/supabase-data-client';
import { NoteCategory } from '../../types/notes';
import { useCacheStore } from './useCacheStore';
import { useCacheFirstQuery } from './useCacheFirstQuery';

interface UseNoteCategoriesReturn {
  categories: NoteCategory[];
  loading: boolean;
  error: PostgrestError | null;
  refetch: () => Promise<void>;
}

/**
 * Cache-first list of note categories under a given parent (null = roots).
 * Mirrors useCategories for the notes tree.
 */
const useNoteCategories = (
  parentId: string | null,
): UseNoteCategoriesReturn => {
  const cache = useCacheStore();

  const cacheReader = cache.getNoteCategories;

  const cacheFilter = useCallback(
    (items: NoteCategory[]) =>
      items
        .filter((c) =>
          parentId === null ? c.parent_id === null : c.parent_id === parentId,
        )
        .sort((a, b) => a.category_name.localeCompare(b.category_name)),
    [parentId],
  );

  const networkFetch = useCallback(async () => {
    const { data, error } = await supabaseData
      .from('note_categories')
      .select('*')
      .order('category_name');

    if (data) {
      cache.setNoteCategories(data as NoteCategory[]);
      const filtered = (data as NoteCategory[]).filter((c) =>
        parentId === null ? c.parent_id === null : c.parent_id === parentId,
      );
      return { data: filtered, error };
    }

    return { data: data as NoteCategory[] | null, error };
  }, [cache, parentId]);

  const { data, loading, error, refetch } = useCacheFirstQuery<NoteCategory>({
    cacheReader,
    cacheFilter,
    networkFetch,
    queryKey: parentId ?? '__root__',
  });

  return { categories: data ?? [], loading, error, refetch };
};

export default useNoteCategories;
